import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Snackbar,
    Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { sendContactMessage } from '../Services/ApiCalls';

const ContactForm = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !message) {
            setSnackbarMessage('Please fill in all fields');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            return;
        }

        setLoading(true);
        try {
            await sendContactMessage(email, message);
            setSnackbarMessage('Message sent successfully! We will get back to you soon.');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            setEmail('');
            setMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            setSnackbarMessage('Failed to send message. Please try again later.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <Paper
            elevation={3}
            sx={{
                p: { xs: 3, md: 5 },
                mt: 6,
                mb: 6,
                maxWidth: 800,
                mx: 'auto',
                borderRadius: 2
            }}
        >
            <Typography
                variant="h4"
                component="h2"
                align="center"
                gutterBottom
                sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: 'text.primary'
                }}
            >
                Contact Us
            </Typography>
            <Typography
                variant="body1"
                align="center"
                sx={{ mb: 4, color: 'text.secondary' }}
            >
                Have a question or feedback? We'd love to hear from you.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                    fullWidth
                    label="Your Email"
                    variant="outlined"
                    margin="normal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    type="email"
                    required
                    sx={{ mb: 3 }}
                />
                <TextField
                    fullWidth
                    label="Your Message"
                    variant="outlined"
                    margin="normal"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={loading}
                    multiline
                    rows={4}
                    required
                    sx={{ mb: 4 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={<SendIcon />}
                        disabled={loading}
                        sx={{
                            px: 5,
                            py: 1.5,
                            borderRadius: 8,
                            textTransform: 'none',
                            fontSize: '1.1rem'
                        }}
                    >
                        {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                </Box>
            </Box>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ContactForm;
